import { Box, Button, Dialog, Link, Typography } from '@robocorp/components';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { IconArrowUpRight } from '@robocorp/icons/iconic';
import { AppState } from '~/store';
import { uiActions, userActions } from '../actions';
import { ExternalLinks } from '../constants';

import slackIcon from '../assets/slackIcon.png';
import { ListContent } from './old-ds-components';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

const SlackInviter: FC<Props> = ({
  email,
  slackDialogOpen,
  toggleSlackDialog,
  requestInvite,
  showInvite,
  requestingInvite,
}) => {
  const { t } = useTranslation();
  const [inviteRequested, setInviteRequested] = useState(false);

  useEffect(() => {
    if (window.location?.hash?.includes('join-slack')) {
      toggleSlackDialog();
    }
  }, [window.location]);

  const handleHashChange = useCallback((event: HashChangeEvent) => {
    if (event.newURL.includes('join-slack')) toggleSlackDialog();
  }, []);

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [handleHashChange]);

  const onDialogClose = () => {
    toggleSlackDialog();
  };

  const onSendInvite = async () => {
    await requestInvite();
    setInviteRequested(true);
  };

  return (
    <Dialog title={t('joinCommunity')} open={slackDialogOpen} onClose={onDialogClose} width={500}>
      <Dialog.Content>
        <Box textAlign="center">
          <Box mt="$36" mb="$24">
            <img src={slackIcon} alt={t('joinCommunity')} />
          </Box>
          <Typography as="h3">{t('joinCommunity')}</Typography>
          <Box pt={12}>
            <Typography>{t('joinCommunityDesc')}</Typography>
          </Box>
          <Box pt={24}>
            {showInvite && !inviteRequested && (
              <Button onClick={onSendInvite} loading={requestingInvite}>
                {t('sendInvite')}
              </Button>
            )}
            {!showInvite && inviteRequested && (
              <Box p={12} width={350} backgroundColor="background.primary" margin="0 auto">
                <ListContent type="text" secondaryTop={t('inviteSent')}>
                  {email}
                </ListContent>
              </Box>
            )}
            {!showInvite && !inviteRequested && (
              <Box p={12} width={350} backgroundColor="background.primary" margin="0 auto">
                <ListContent type="text" secondaryTop={t('alreadyJoined')}>
                  {email}
                </ListContent>
              </Box>
            )}
          </Box>
          <Box py="$24">
            <Link
              variant="subtle"
              iconAfter={IconArrowUpRight}
              href={ExternalLinks.SLACK}
              target="_blank"
              rel="noreferrer"
            >
              {t('openSlack')}
            </Link>
          </Box>
          {!showInvite && (
            <Typography fontSize="$12">
              <Link href={ExternalLinks.SUPPORT} color="grey30">
                {t('contactSupport')}
              </Link>
            </Typography>
          )}
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

function mapStateToProps(state: AppState) {
  const details = state.userResources.users.current.details || {};
  const showInvite = !details.slack_invite_requested;
  const { slackDialogOpen } = state.ui;
  const requestingInvite = state.entityStates.slack.requestingSlackInvite;

  return {
    email: details.email,
    showInvite,
    slackDialogOpen,
    requestingInvite,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      toggleSlackDialog: uiActions.toggleSlackDialog,
      requestInvite: userActions.requestSlackInvite,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(SlackInviter);
